<script setup lang="ts">
    import { Company } from '@/types';
    import { useForm } from '@inertiajs/vue3';
    import { useQueryClient } from '@tanstack/vue-query';
    import { useI18n } from 'vue-i18n';
    import {
        AlertDialog,
        AlertDialogTrigger,
        AlertDialogContent,
        AlertDialogHeader,
        AlertDialogTitle,
        AlertDialogDescription,
        AlertDialogFooter,
        AlertDialogCancel,
        AlertDialogAction,
    } from '@/components/ui/alert-dialog';
    import { Button } from '@/components/ui/button';
    import { Check } from 'lucide-vue-next';
    import { toast } from 'vue-sonner';
    import QueryKey from '@/enums/QueryKey';

    const { t } = useI18n();

    const queryClient = useQueryClient();

    const props = defineProps<{
        company: Company;
    }>();

    const form = useForm({
        id: props.company.id,
    });

    const restoreCompany = () => {
        form.post(route('v2.companies.restore'), {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: [QueryKey.COMPANIES] });
                toast.success(t(`companies.js.restore.success`));
            },
            onError: () => {
                toast.error(t('forms.general.error'));
            },
        });
    };
</script>

<template>
    <AlertDialog>
        <AlertDialogTrigger as-child>
            <Button
                variant="ghost"
                class="mr-auto"
            >
                <span>{{ $t('forms.restore') }}</span>
                <Check class="text-accent" />
            </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
            <AlertDialogHeader>
                <AlertDialogTitle>{{ t(`companies.js.restore.title`) }}</AlertDialogTitle>
                <AlertDialogDescription>{{
                    t(`companies.js.restore.content`)
                }}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
                <AlertDialogCancel>{{ t(`companies.js.restore.no`) }}</AlertDialogCancel>
                <AlertDialogAction
                    @click="restoreCompany"
                    variant="default"
                >
                    {{ t(`companies.js.restore.yes`) }}
                </AlertDialogAction>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
</template>
